import { computed } from 'vue';
import { store } from '@/store/index';
import { readAssertedUserProfile } from '@/store/main/getters';
import { RoleId } from '@/lib/enum';
import { Department } from '@/interfaces/model/general/department';
import { defineMessages } from '@/lib/i18n';
import sharedMessages from '@/lib/shared-messages';

const m = defineMessages({
  view: '閲覧',
  editOwnDepartment: '編集(自部署)',
  editOtherDepartment: '編集(他部署)',
  upload: 'アップロード',
  requestOwnDepartment: '再データ化依頼(自部署)',
  requestOtherDepartment: '再データ化依頼(他部署)',
  companyEdit: '会社編集',
  sds: 'SDS',
  createSimple: 'CREATE-SIMPLE',
  user: 'ユーザー',
  request: 'リクエスト',
});

const PERMISSIONS = {
  ALL_PRIVILEGES: 'ALL_PRIVILEGES',
  ALLOWED_IP_ADDRESS_CREATE: 'ALLOWED_IP_ADDRESS_CREATE',
  ALLOWED_IP_ADDRESS_DELETE: 'ALLOWED_IP_ADDRESS_DELETE',
  COMPANY_EDIT: 'COMPANY_EDIT',
  COMPANY_DELETE: 'COMPANY_DELETE',
  SDS_UPLOAD: 'SDS_UPLOAD',
  SDS_READ: 'SDS_READ',
  SDS_EDIT_OWN_DEPARTMENT: 'SDS_EDIT_OWN_DEPARTMENT',
  SDS_EDIT_OTHER_DEPARTMENT: 'SDS_EDIT_OTHER_DEPARTMENT',
  SDS_DELETE: 'SDS_DELETE',
  SDS_EXPORT: 'SDS_EXPORT',
  SDS_DOWNLOAD: 'SDS_DOWNLOAD',
  LABEL_DOWNLOAD: 'LABEL_DOWNLOAD',
  SDS_REQUEST_OWN_DEPARTMENT: 'SDS_REQUEST_OWN_DEPARTMENT',
  SDS_REQUEST_OTHER_DEPARTMENT: 'SDS_REQUEST_OTHER_DEPARTMENT',
  RISK_ASSESSMENT_WRITE_OWN_DEPARTMENT: 'RISK_ASSESSMENT_WRITE_OWN_DEPARTMENT',
  RISK_ASSESSMENT_WRITE_OTHER_DEPARTMENT:
    'RISK_ASSESSMENT_WRITE_OTHER_DEPARTMENT',
  CHECK_LIST_READ: 'CHECK_LIST_READ',
  CHECK_LIST_REQUEST: 'CHECK_LIST_REQUEST',
  CHECK_LIST_EXPORT: 'CHECK_LIST_EXPORT',
  CHECK_LIST_CREATE: 'CHECK_LIST_CREATE',
  CHECK_LIST_DELETE: 'CHECK_LIST_DELETE',
  CHECK_LIST_EDIT: 'CHECK_LIST_EDIT',
  CREATE_SIMPLE_DOWNLOAD: 'CREATE_SIMPLE_DOWNLOAD',
  TAG_CREATE: 'TAG_CREATE',
  TAG_EDIT: 'TAG_EDIT',
  TAG_DELETE: 'TAG_DELETE',
  DEPARTMENT_CREATE: 'DEPARTMENT_CREATE',
  DEPARTMENT_EDIT: 'DEPARTMENT_EDIT',
  DEPARTMENT_EDIT_OTHER_DEPARTMENT: 'DEPARTMENT_EDIT_OTHER_DEPARTMENT',
  DEPARTMENT_DELETE: 'DEPARTMENT_DELETE',
  USER_EDIT: 'USER_EDIT',
  USER_INVITE: 'USER_INVITE',
  USER_DELETE: 'USER_DELETE',
  USER_DEACTIVATE: 'USER_DEACTIVATE',
  USER_PASSWORD_RESET: 'USER_PASSWORD_RESET',
  USER_READ_2FA_INFO: 'USER_READ_2FA_INFO',
  USER_READ_LOGIN_AT: 'USER_READ_LOGIN_AT',
  PASSWORD_POLICY_EDIT: 'PASSWORD_POLICY_EDIT',
  ENFORCE_2FA_AUTHENTICATION: 'ENFORCE_2FA_AUTHENTICATION',
} as const;
export type Permission = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

const ROLE_PERMISSIONS_DESCRIPTION = {
  [RoleId.USER_VIEWER.value]: [
    {
      feature: m.sds,
      actions: [m.view],
    },
    {
      feature: sharedMessages.checklist,
      actions: [m.view],
    },
  ],
  [RoleId.USER_DOWNLOADABLE_VIEWER.value]: [
    {
      feature: m.sds,
      actions: [m.view, sharedMessages.export],
    },
    {
      feature: sharedMessages.checklist,
      actions: [m.view, sharedMessages.export],
    },
    {
      feature: 'CREATE SIMPLE',
      actions: [sharedMessages.download],
    },
  ],
  [RoleId.USER_EDITOR.value]: [
    {
      feature: m.sds,
      actions: [
        m.view,
        m.editOwnDepartment,
        sharedMessages.delete,
        m.upload,
        sharedMessages.export,
        m.requestOwnDepartment,
      ],
    },
    {
      feature: sharedMessages.checklist,
      actions: [m.view, sharedMessages.export],
    },
    {
      feature: m.createSimple,
      actions: [sharedMessages.download],
    },
    {
      feature: sharedMessages.tag,
      actions: [
        sharedMessages.create,
        sharedMessages.edit,
        sharedMessages.delete,
      ],
    },
  ],
  [RoleId.USER_ADMIN.value]: [
    {
      feature: m.sds,
      actions: [
        m.view,
        m.editOwnDepartment,
        m.editOtherDepartment,
        sharedMessages.delete,
        m.upload,
        sharedMessages.export,
        m.requestOwnDepartment,
        m.requestOtherDepartment,
      ],
    },
    {
      feature: sharedMessages.checklist,
      actions: [m.view, sharedMessages.edit, sharedMessages.export, m.request],
    },
    {
      feature: m.createSimple,
      actions: [sharedMessages.download],
    },
    {
      feature: sharedMessages.tag,
      actions: [
        sharedMessages.create,
        sharedMessages.edit,
        sharedMessages.delete,
      ],
    },
    {
      feature: sharedMessages.whitelistIpAddress,
      actions: [sharedMessages.create, sharedMessages.delete],
    },
    {
      feature: sharedMessages.department,
      actions: [
        sharedMessages.create,
        sharedMessages.edit,
        sharedMessages.delete,
      ],
    },
    {
      feature: m.user,
      actions: [
        sharedMessages.invite,
        sharedMessages.edit,
        sharedMessages.delete,
      ],
    },
    {
      feature: sharedMessages.company,
      actions: [m.companyEdit],
    },
  ],
} as const;

export const usePermissionComposable = () => {
  const rolePermissionsDescription = computed(() => {
    return {
      ...ROLE_PERMISSIONS_DESCRIPTION,
      [RoleId.USER_EDITOR.value]: [
        ...ROLE_PERMISSIONS_DESCRIPTION[RoleId.USER_EDITOR.value],
        {
          feature: sharedMessages.riskAssessment,
          actions: [m.editOwnDepartment],
        },
      ],
      [RoleId.USER_ADMIN.value]: [
        ...ROLE_PERMISSIONS_DESCRIPTION[RoleId.USER_ADMIN.value],
        {
          feature: sharedMessages.riskAssessment,
          actions: [m.editOwnDepartment, m.editOtherDepartment],
        },
      ],
    };
  });

  const userPermissions = computed(() => {
    return readAssertedUserProfile(store).role.permissions.map((p) => p.id);
  });

  const hasPermission = (required: Permission | Permission[]) => {
    if (userPermissions.value.includes(PERMISSIONS.ALL_PRIVILEGES)) {
      return true;
    }

    if (Array.isArray(required)) {
      return required.every((p) => userPermissions.value.includes(p));
    }
    return userPermissions.value.includes(required);
  };

  const hasSdsEditPermission = (sdsDepartments: Pick<Department, 'uuid'>[]) => {
    const userProfile = readAssertedUserProfile(store);
    const scopedDepartmentIds = userProfile.scoped_department_ids || [];
    const sdsDepartmentIds = sdsDepartments.map((d) => d.uuid);
    const hasOwnDepartment = sdsDepartmentIds.some((d) =>
      scopedDepartmentIds.includes(d)
    );

    if (
      !hasPermission(PERMISSIONS.SDS_EDIT_OWN_DEPARTMENT) &&
      !hasPermission(PERMISSIONS.SDS_EDIT_OTHER_DEPARTMENT)
    ) {
      return false;
    }
    if (
      hasOwnDepartment &&
      hasPermission(PERMISSIONS.SDS_EDIT_OWN_DEPARTMENT)
    ) {
      return true;
    }
    if (
      !hasOwnDepartment &&
      hasPermission(PERMISSIONS.SDS_EDIT_OTHER_DEPARTMENT)
    ) {
      return true;
    }
    if (sdsDepartments.length === 0) {
      return true;
    }
    return false;
  };

  const hasSdsRequestPermission = (
    sdsDepartments: Pick<Department, 'uuid'>[]
  ) => {
    const userProfile = readAssertedUserProfile(store);
    const scopedDepartmentIds = userProfile.scoped_department_ids || [];
    const sdsDepartmentIds = sdsDepartments.map((d) => d.uuid);
    const hasOwnDepartment = sdsDepartmentIds.some((d) =>
      scopedDepartmentIds.includes(d)
    );

    if (
      !hasPermission(PERMISSIONS.SDS_REQUEST_OWN_DEPARTMENT) &&
      !hasPermission(PERMISSIONS.SDS_REQUEST_OTHER_DEPARTMENT)
    ) {
      return false;
    }
    if (
      hasOwnDepartment &&
      hasPermission(PERMISSIONS.SDS_REQUEST_OWN_DEPARTMENT)
    ) {
      return true;
    }
    if (
      !hasOwnDepartment &&
      hasPermission(PERMISSIONS.SDS_REQUEST_OTHER_DEPARTMENT)
    ) {
      return true;
    }
    if (sdsDepartments.length === 0) {
      return true;
    }
    return false;
  };

  return {
    hasPermission,
    hasSdsEditPermission,
    hasSdsRequestPermission,
    PERMISSIONS,
    ROLE_PERMISSIONS_DESCRIPTION: rolePermissionsDescription,
  };
};
